import { useEffect, useState } from 'react';
import { deleteReorderItem, updateReorderItem } from '../../lib/reorder';
import Badge from '../Badge';
import { resolvePreviews } from '../../lib/linkPreview';
import { toHttpUrl, prettyUrl } from '../../lib/url';
import LinkThumbnail from './LinkThumbnail';
import QuickAddItem from './QuickAddItem';
import Pagination, { usePagination } from '../Pagination';
import { todayISO as today } from '../../lib/dates';
import { useConfirm } from '../../components/ConfirmDialog';

// Ordered is in-flight, Received is done, Dismissed is deliberately dropped.
const STATUS_TONE = { Ordered: 'warning', Received: 'success', Dismissed: 'muted' };

// The running list of things to buy.
//
// Adding lives in QuickAddItem: you notice you're out of vanilla oil at the
// bench, and the thought should cost a few seconds to capture. Everything
// except the name is optional — a PO can fill in the rest later.
export default function ToOrderList({
  items,
  vendors,
  loading,
  error,
  onChanged,
  onCreatePO,
  onCreateVendor,
}) {
  const confirm = useConfirm();
  const [selected, setSelected] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [previews, setPreviews] = useState({});

  // Resolve thumbnails for whatever links are on the list. Cached rows come
  // back in one query; only genuinely new links cost a fetch.
  useEffect(() => {
    const urls = items.map((i) => i.link_url).filter(Boolean);
    if (urls.length === 0) return;
    let live = true;
    resolvePreviews(urls, {
      onResolved: (next) => {
        if (live) setPreviews(next);
      },
    }).catch(() => {});
    return () => {
      live = false;
    };
  }, [items]);

  const needed = items.filter((i) => i.status === 'Needed');
  const done = items.filter((i) => i.status !== 'Needed');
  const visible = showDone ? done : needed;

  // Same shared paging the Purchase Orders and Vendors tabs use. Handled is
  // the tab that actually grows without limit — every item ever received or
  // dismissed lands there and never leaves.
  const { visible: pageItems, controls } = usePagination(visible);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Deliberately over `needed`, not the current page: a selection made on
  // page 1 has to survive paging to page 2, or a multi-page order can't be
  // assembled at all.
  const selectedItems = needed.filter((i) => selected.includes(i.id));

  // A PO belongs to one vendor, so a mixed selection can't become one order.
  const selectedVendorIds = [...new Set(selectedItems.map((i) => i.vendor_id || 'none'))];
  const mixedVendors = selectedVendorIds.length > 1;

  return (
    <section className="panel">
      <QuickAddItem
        items={items}
        vendors={vendors}
        onCreateVendor={onCreateVendor}
        onChanged={onChanged}
      />

      {error && <p className="po-error">{error}</p>}

      <div className="toorder-toolbar">
        <div className="toorder-tabs">
          <button
            className={'batch-filter' + (!showDone ? ' active' : '')}
            onClick={() => setShowDone(false)}
          >
            To order ({needed.length})
          </button>
          <button
            className={'batch-filter' + (showDone ? ' active' : '')}
            onClick={() => setShowDone(true)}
          >
            Handled ({done.length})
          </button>
        </div>

        {selectedItems.length > 0 && (
          <div className="toorder-selection">
            {mixedVendors ? (
              <span className="toorder-warning">
                Selected items span different vendors — a purchase order goes to one vendor, so
                select items from a single vendor.
              </span>
            ) : (
              <button className="ghost-button" onClick={() => onCreatePO(selectedItems)}>
                Create purchase order from {selectedItems.length}{' '}
                {selectedItems.length === 1 ? 'item' : 'items'}
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <p className="po-loading">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="po-empty">
          {showDone
            ? 'Nothing handled yet.'
            : 'Nothing on the list — add anything you notice running low.'}
        </p>
      ) : (
        <ul className="toorder-list">
          {pageItems.map((item) => {
            const overdue =
              item.needed_by && item.status === 'Needed' && item.needed_by < today();
            return (
              <li className={'toorder-item' + (overdue ? ' overdue' : '')} key={item.id}>
                {!showDone && (
                  <input
                    type="checkbox"
                    checked={selected.includes(item.id)}
                    onChange={() => toggle(item.id)}
                    aria-label={`Select ${item.item_name}`}
                  />
                )}

                {item.link_url && (
                  <LinkThumbnail
                    href={toHttpUrl(item.link_url)}
                    preview={previews[toHttpUrl(item.link_url)]}
                    alt={item.item_name}
                  />
                )}

                <div className="toorder-item-main">
                  <span className="toorder-item-name">{item.item_name}</span>
                  <span className="toorder-item-meta">
                    {item.quantity && <span>{item.quantity}</span>}
                    {item.vendor?.name && <span>{item.vendor.name}</span>}
                    {item.needed_by && <span>by {item.needed_by}</span>}
                    {toHttpUrl(item.link_url) && (
                      <a
                        className="toorder-item-link"
                        href={toHttpUrl(item.link_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {prettyUrl(item.link_url)}
                      </a>
                    )}
                    {item.status !== 'Needed' && (
                      <Badge tone={STATUS_TONE[item.status] || 'neutral'}>{item.status}</Badge>
                    )}
                  </span>
                  {item.notes && <span className="toorder-item-notes">{item.notes}</span>}
                </div>

                <div className="toorder-item-actions">
                  {item.status === 'Needed' ? (
                    <button
                      onClick={async () => {
                        await updateReorderItem(item.id, { status: 'Dismissed' });
                        await onChanged();
                      }}
                    >
                      Dismiss
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        await updateReorderItem(item.id, {
                          status: 'Needed',
                          purchase_order_id: null,
                        });
                        await onChanged();
                      }}
                    >
                      Put back
                    </button>
                  )}
                  <button
                    className="danger"
                    onClick={async () => {
                      if (!(await confirm({
                        title: 'Delete this item?',
                      }))) return;

                      await deleteReorderItem(item.id);
                      await onChanged();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination {...controls} />
    </section>
  );
}
